import { transformEnumToDisplay } from '@fruitful/member-profile';
import { useAtomValue } from 'jotai/index';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
} from 'primereact/datatable';
import { useState } from 'react';
import { H6Emphasized, Spinner, useToastController, YStack } from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { formatToSimpleDateString } from '../../common/utils/formatting';
import {
    adminUserAtom,
    selectedMembershipAtom,
} from '../../entities/entityAtoms';
import { type MemberDetails } from '../../entities/entityTypes';
import { EmploymentStatusEditor } from '../../member/components/editors/EmploymentStatusEditor';
import { PronounsEditor } from '../../member/components/editors/PronounsEditor';
import { TaxFilingEditor } from '../../member/components/editors/TaxFilingEditor';
import { useUpdateMemberAndRefetch } from '../../member/hooks/useUpdateMember';

interface MemberProfileMemberTableProps {
    members: MemberDetails[];
    timezone?: string;
}

// A slimmed down version of the MemberInfo table component.
export const MemberProfileMemberTable = ({
    members,
    timezone,
}: MemberProfileMemberTableProps) => {
    const toast = useToastController();

    const adminUser = useAtomValue(adminUserAtom);
    const canEditMember =
        adminUser.authorization.selectedMembership.general.canEdit;

    const membership = useAtomValue(selectedMembershipAtom);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const { updateMemberAsync } = useUpdateMemberAndRefetch(membership);

    const onRowEditComplete = async ({
        newData,
    }: DataTableRowEditCompleteEvent) => {
        setIsSubmitting(true);
        const member: MemberDetails = newData as MemberDetails;

        try {
            const upsertMemberBody = {
                pronouns: member.pronouns,
                employmentStatus: member.employmentStatus,
                taxFiling: member.taxFiling,
            };
            await updateMemberAsync(member.id, upsertMemberBody);

            toast.show('Member updated!', MutationSuccessToastOptions);
        } catch (error) {
            toast.show('Error updating member', MutationErrorToastOptions());
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <YStack gap={12}>
            <H6Emphasized>Member details</H6Emphasized>
            {isSubmitting ? (
                <Spinner />
            ) : (
                <DataTable
                    value={members}
                    size="small"
                    tableStyle={{ width: '100%', fontSize: 13 }}
                    editMode="row"
                    onRowEditComplete={onRowEditComplete}
                    scrollable
                >
                    <Column field="id" header="id" hidden={true} />
                    <Column field="firstName" header="First Name" />
                    <Column field="lastName" header="Last Name" />
                    <Column
                        field="timezone"
                        header="Timezone"
                        body={timezone ?? 'Unknown'}
                    />
                    <Column
                        field="dateOfBirth"
                        header="Date of Birth"
                        body={({ dateOfBirth }: MemberDetails) => {
                            return formatToSimpleDateString(dateOfBirth, {
                                timeZone: 'UTC',
                            });
                        }}
                    />
                    <Column
                        field="pronouns"
                        header="Pronouns"
                        editor={(options) => PronounsEditor(options)}
                    />
                    <Column
                        field="taxFiling"
                        header="Tax Filing"
                        body={({ taxFiling }: MemberDetails) => {
                            return taxFiling != null
                                ? transformEnumToDisplay(taxFiling)
                                : '';
                        }}
                        editor={(options) => TaxFilingEditor(options)}
                        style={{
                            minWidth: 150,
                        }}
                    />
                    <Column
                        field="employmentStatus"
                        header="Employment Status"
                        body={({ employmentStatus }: MemberDetails) => {
                            return employmentStatus != null
                                ? transformEnumToDisplay(employmentStatus)
                                : '';
                        }}
                        editor={(options) => EmploymentStatusEditor(options)}
                        style={{
                            minWidth: 150,
                        }}
                    />
                    <Column rowEditor hidden={!canEditMember} />
                </DataTable>
            )}
        </YStack>
    );
};
